import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { FormHelper } from '@app/ui-forms/classes/form-helper';
import { DomainHelper } from '@app/core/helpers/domain.helper';

import { ScriptLoaderService } from '@app/core/services/script-loader.service';
import { ConfigureService } from '@app/configure/services/configure.service';
import { TrackingService } from '@app/core/services/tracking.service';
import { SessionService } from '@app/core/services/session.service';
import { HelperService } from '@app/core/services/helper.service';
import { ToastService } from '@app/core/services/toast.service';

import { FormInputEvent } from '@app/ui-forms/models/form-input-event.model';
import { Dealer } from '@app/core/models/dealer.model';

import { environment } from '@environments/environment';

@Component({
	selector: 'cng-stripe',
	templateUrl: './stripe.component.html',
	styleUrls: ['./stripe.component.less'],
})
export class ConfigureStripeComponent implements OnInit, OnDestroy {
	@Input() key: string = '';
	@Input() test_mode: boolean = false;
	@Input() payment_type: 'buyNow' | 'reserve' = 'reserve';

	stripe_form: FormGroup;
	stripe: any = null;
	card: any = null;
	enquiry_hash: string = this.configure_service.configure_data.meta.enquiry_hash;
	customer: any = this.configure_service.configure_data.meta.customer;
	payment: any = this.helper_service.copy(this.configure_service.configure_data.steps.payment.data);
	vehicle: any = this.configure_service.configure_data.steps.purchase_vehicle.data;
	reservation_amount: number =
		this.configure_service.configure_data.steps.payment.data.reservation.reservation_amount;
	grand_total: number = this.configure_service.configure_data.steps.customise_review.data.grand_total;
	dealer: Dealer = this.session_service.getDealer();
	stripe_logo: string = `${environment.deploy_protocol}${environment.deploy_domain}/assets/img/stripe.png`;

	card_style: any = {
		style: {
			base: {
				lineHeight: '36px',
				fontSize: '16px',
				fontWeight: 400,
				fontFamily: '"Roboto", "Trebuchet", Arial, sans-serif',
				'::placeholder': {
					color: '#BBB',
				},
			},
			invalid: {
				iconColor: '#de3c3c',
				color: '#de3c3c',
			},
		},
		classes: {
			focus: 'is-focused',
			empty: 'is-empty',
		},
	};

	card_details_invalid: boolean = false;
	card_details_empty: boolean = true;
	sending: boolean = false;
	tracking_log: string[] = [];

	destroyed$: Subject<boolean> = new Subject<boolean>();

	constructor(
		private script_loader_service: ScriptLoaderService,
		private element_ref: ElementRef,
		private configure_service: ConfigureService,
		private helper_service: HelperService,
		private toast_service: ToastService,
		private tracking_service: TrackingService,
		private session_service: SessionService
	) {}

	ngOnInit(): void {
		this.buildForm();
		this.initStripe();

		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure_data) => {
			this.enquiry_hash = configure_data.meta.enquiry_hash;
			this.customer = configure_data.meta.customer;
			this.vehicle = configure_data.steps.purchase_vehicle.data;
			this.payment = this.helper_service.copy(configure_data.steps.payment.data);
			this.buildForm();
		});
	}

	buildForm(): void {
		this.stripe_form = new FormGroup({
			first_name: new FormControl(this.customer.first_name, Validators.required),
			last_name: new FormControl(this.customer.last_name, Validators.required),
			email: new FormControl(
				this.customer.email === null ? this.customer.email : this.customer.email.toLowerCase(),
				[Validators.required, Validators.email]
			),
			tel_mob: new FormControl(this.customer.tel_mob, [Validators.required, Validators.pattern('[0-9 +]+')]),
			terms: new FormControl(false, [Validators.requiredTrue]),
		});
	}

	initStripe(): void {
		this.script_loader_service
			.load({ name: 'stripe' })
			.then(() => {
				this.stripe = (<any>window).Stripe(this.key);
				const elements = this.stripe.elements();

				this.card = elements.create('card', this.card_style);
				this.card.mount(this.element_ref.nativeElement.querySelector('#card-element-stripe'));

				this.card.on('change', (e) => {
					this.validateCardDetails(e);
					this.trackCardInput();
				});
			})
			.catch((error) => {
				throw new Error(error);
			});
	}

	validateCardDetails(data): void {
		this.card_details_empty = data.empty;
		this.card_details_invalid = typeof data.error !== 'undefined';
	}

	onSubmit(): void {
		this.tracking_service.trackFormSubmitAttempt(
			'stripe_form',
			FormHelper.getCompletedControls(this.stripe_form),
			FormHelper.getEmptyControls(this.stripe_form)
		);
		if (this.stripe !== null && this.card !== null && this.stripe_form.valid) {
			this.sending = true;

			this.stripe.createPaymentMethod('card', this.card).then((data) => {
				if (!data.error) {
					this.tracking_service.trackFormValidationPass('stripe_form');
					this.createPayment(data.paymentMethod);
					return;
				}

				this.card_details_invalid = true;
				FormHelper.forceValidate(this.stripe_form);
				this.trackValidationFailure();
				this.toast_service.error('Form has invalid fields');
				this.sending = false;
			});
		} else {
			FormHelper.forceValidate(this.stripe_form);
			this.trackValidationFailure();
			this.toast_service.error('Form has invalid fields');
		}
	}

	createPayment(token: any): void {
		const body = {
			payment: {
				service: 'stripe',
				token: token.id,
			},
			customer: {
				first_name: this.stripe_form.controls['first_name'].value,
				last_name: this.stripe_form.controls['last_name'].value,
				email: this.stripe_form.controls['email'].value.toLowerCase().replace(/ /g, ''),
				tel_mob: this.stripe_form.controls['tel_mob'].value.toLowerCase().replace(/ /g, ''),
			},
		};

		this.configure_service.configure_data.meta.customer = body.customer;

		this.configure_service.createPaymentCharge(body, this.payment_type, true).subscribe((response: any) => {
			if (response) {
				this.handlePaymentResponse(response);
			} else {
				this.configure_service.setSoftError({
					title: 'Payment Not Taken',
					message:
						"We're sorry, we were unable to take your payment at this time. Please arrange a callback so we can discuss your reservation.",
				});
			}
		});
	}

	validatePaymentIntent(response: any) {
		this.stripe.handleCardAction(response.token).then((result) => {
			if (result.error) {
				this.showSoftError('failed_3d_secure_authentication');
				this.sending = false;
				return;
			}

			const body = {
				payment: {
					service: 'stripe',
					token: response.id,
					method: response.method,
					type: this.payment_type === 'buyNow' ? 'full_payment' : 'reservation',
				},
			};

			this.configure_service.validatePaymentCharge(body, this.payment_type, true).subscribe((response: any) => {
				this.handlePaymentResponse(response);
			});
		});
	}

	handlePaymentResponse(response) {
		if (response !== true && response.status && response.status === 'requires_action') {
			this.validatePaymentIntent(response);
			this.tracking_service.trackFormSubmitFailure('stripe_form', 'requires_action');
			return;
		}

		if (response === true) {
			this.toast_service.success('Payment successful!');

			if (this.payment_type === 'buyNow') {
				this.tracking_service.trackFullPayment(this.grand_total);
			} else {
				this.tracking_service.trackReservationPayment(this.reservation_amount);
			}
			this.tracking_service.trackFormSubmitSuccess('stripe_form');
		} else {
			this.showSoftError('payment_failed');
		}
		this.sending = false;
	}

	showSoftError(reason: string): void {
		this.configure_service.setSoftError({
			title: 'Payment failed',
			message:
				"We're sorry, we were unable to take payment at this time using the details you provided. Please arrange a callback so we can discuss your reservation.",
		});
		this.tracking_service.trackFormSubmitFailure('stripe_form', reason);
	}

	onTermsCta(event: any): void {
		if (event === 'terms-and-conditions') {
			window.open(this.dealer.terms_and_conditions_url);
			return;
		}
		window.open(`//${window.location.host}${DomainHelper.getBaseHref()}configure/${event}`, '_blank');
	}

	onFormInput(form: any): void {
		const event: FormInputEvent = FormHelper.getInputEvent(form);
		if (this.tracking_log.indexOf(event.log_name) === -1) {
			this.tracking_service.trackFormInput(event.form_name, event.input_type, event.input_name);
			this.tracking_log.push(event.log_name);
		}
	}

	trackCardInput(): void {
		const log_name: string = `stripe_form__card_control__stripe`;
		if (this.tracking_log.indexOf(log_name) === -1) {
			this.tracking_service.trackFormInput('configure_stripe_form', 'card_control', 'stripe');
			this.tracking_log.push(log_name);
		}
	}

	trackValidationFailure(): void {
		const invalid_fields: string[] = FormHelper.getInvalidControls(this.stripe_form);
		if (this.card_details_invalid) {
			invalid_fields.push('stripe');
		}
		this.tracking_service.trackFormValidationFailure(
			'stripe_form',
			'invalid_fields[' + invalid_fields.join(',') + ']'
		);
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
	}
}
